import { Locale } from "../Locale.ts";

/**
 * UI strings for the daily puzzle page (`src/DailyPuzzle/**`, ADR 0127). The
 * page is English-only for now, so Spanish and French are here to keep the
 * catalogue complete (ADR 0051), not because a localised route exists.
 */
const en = {
    "dailyPuzzle.howToPlay":
        "Sixteen tiles make four groups of four, and every tile in a group has the same value. Select four tiles and press Check. You have four mistakes.",
    "dailyPuzzle.levelsLabel": "Level",
    "dailyPuzzle.gradeTemplate": "Grade {n}",
    "dailyPuzzle.titleTemplate": "Equal Groups #{n}",
    "dailyPuzzle.practiceTitle": "Practice puzzle",
    "dailyPuzzle.boardAria": "Equal Groups board. Select four tiles that have the same value, then press Check.",
    "dailyPuzzle.check": "Check",
    "dailyPuzzle.clear": "Clear",
    "dailyPuzzle.shuffle": "Shuffle",
    "dailyPuzzle.mistakesLabel": "Mistakes left",
    "dailyPuzzle.mistakesAriaTemplate": "Mistakes left: {n} of {total}",
    "dailyPuzzle.foundValueTemplate": "= {value}",
    "dailyPuzzle.foundAnnounceTemplate": "Group found. All four are equal to {value}.",
    "dailyPuzzle.wrongThree": "Three of these are equal — one belongs elsewhere.",
    "dailyPuzzle.wrongTwo": "Only two of these are equal.",
    "dailyPuzzle.wrongNone": "No two of these are equal.",
    "dailyPuzzle.mistakeLeftOne": "1 mistake left.",
    "dailyPuzzle.mistakesLeftTemplate": "{n} mistakes left.",
    "dailyPuzzle.repeatGuess": "You've already tried those four.",
    "dailyPuzzle.won": "Solved! You found all four groups.",
    "dailyPuzzle.lost": "Out of mistakes. These are the groups you missed.",
    "dailyPuzzle.missedHeading": "Groups you missed",
    "dailyPuzzle.streakOne": "Solved 1 day in a row",
    "dailyPuzzle.streakTemplate": "Solved {n} days in a row",
    "dailyPuzzle.copyResult": "Copy result",
    "dailyPuzzle.copied": "Copied",
    "dailyPuzzle.copyManual": "Copying did not work here. Select and copy this text.",
    "dailyPuzzle.copyAria": "Result to copy",
    "dailyPuzzle.resultHeadingTemplate": "Equal Groups #{n} · {level}",
    "dailyPuzzle.practice": "Play a practice puzzle",
    "dailyPuzzle.practiceAnother": "Play another practice puzzle",
    "dailyPuzzle.backToDaily": "Back to today's puzzle",
} as const;

export type DailyPuzzleStringKey = keyof typeof en;

const es = {
    "dailyPuzzle.howToPlay":
        "Dieciséis fichas forman cuatro grupos de cuatro, y todas las fichas de un grupo tienen el mismo valor. Elige cuatro fichas y pulsa Comprobar. Tienes cuatro errores.",
    "dailyPuzzle.levelsLabel": "Nivel",
    "dailyPuzzle.gradeTemplate": "Grado {n}",
    "dailyPuzzle.titleTemplate": "Grupos iguales n.º {n}",
    "dailyPuzzle.practiceTitle": "Puzle de práctica",
    "dailyPuzzle.boardAria":
        "Tablero de Grupos iguales. Elige cuatro fichas con el mismo valor y pulsa Comprobar.",
    "dailyPuzzle.check": "Comprobar",
    "dailyPuzzle.clear": "Borrar",
    "dailyPuzzle.shuffle": "Mezclar",
    "dailyPuzzle.mistakesLabel": "Errores restantes",
    "dailyPuzzle.mistakesAriaTemplate": "Errores restantes: {n} de {total}",
    "dailyPuzzle.foundValueTemplate": "= {value}",
    "dailyPuzzle.foundAnnounceTemplate": "Grupo encontrado. Las cuatro valen {value}.",
    "dailyPuzzle.wrongThree": "Tres de estas son iguales: una pertenece a otro grupo.",
    "dailyPuzzle.wrongTwo": "Solo dos de estas son iguales.",
    "dailyPuzzle.wrongNone": "Ninguna de estas es igual a otra.",
    "dailyPuzzle.mistakeLeftOne": "Queda 1 error.",
    "dailyPuzzle.mistakesLeftTemplate": "Quedan {n} errores.",
    "dailyPuzzle.repeatGuess": "Ya has probado esas cuatro.",
    "dailyPuzzle.won": "¡Resuelto! Encontraste los cuatro grupos.",
    "dailyPuzzle.lost": "Sin errores restantes. Estos son los grupos que faltaron.",
    "dailyPuzzle.missedHeading": "Grupos que faltaron",
    "dailyPuzzle.streakOne": "Resuelto 1 día seguido",
    "dailyPuzzle.streakTemplate": "Resuelto {n} días seguidos",
    "dailyPuzzle.copyResult": "Copiar resultado",
    "dailyPuzzle.copied": "Copiado",
    "dailyPuzzle.copyManual": "No se pudo copiar aquí. Selecciona y copia este texto.",
    "dailyPuzzle.copyAria": "Resultado para copiar",
    "dailyPuzzle.resultHeadingTemplate": "Grupos iguales n.º {n} · {level}",
    "dailyPuzzle.practice": "Jugar un puzle de práctica",
    "dailyPuzzle.practiceAnother": "Jugar otro puzle de práctica",
    "dailyPuzzle.backToDaily": "Volver al puzle de hoy",
} satisfies Record<DailyPuzzleStringKey, string>;

const fr = {
    "dailyPuzzle.howToPlay":
        "Seize tuiles forment quatre groupes de quatre, et toutes les tuiles d'un groupe ont la même valeur. Choisissez quatre tuiles et appuyez sur Vérifier. Vous avez quatre erreurs.",
    "dailyPuzzle.levelsLabel": "Niveau",
    "dailyPuzzle.gradeTemplate": "Classe {n}",
    "dailyPuzzle.titleTemplate": "Groupes égaux n° {n}",
    "dailyPuzzle.practiceTitle": "Casse-tête d'entraînement",
    "dailyPuzzle.boardAria":
        "Plateau de Groupes égaux. Choisissez quatre tuiles de même valeur, puis appuyez sur Vérifier.",
    "dailyPuzzle.check": "Vérifier",
    "dailyPuzzle.clear": "Effacer",
    "dailyPuzzle.shuffle": "Mélanger",
    "dailyPuzzle.mistakesLabel": "Erreurs restantes",
    "dailyPuzzle.mistakesAriaTemplate": "Erreurs restantes : {n} sur {total}",
    "dailyPuzzle.foundValueTemplate": "= {value}",
    "dailyPuzzle.foundAnnounceTemplate": "Groupe trouvé. Les quatre valent {value}.",
    "dailyPuzzle.wrongThree": "Trois de ces tuiles sont égales : une va ailleurs.",
    "dailyPuzzle.wrongTwo": "Seulement deux de ces tuiles sont égales.",
    "dailyPuzzle.wrongNone": "Aucune de ces tuiles n'est égale à une autre.",
    "dailyPuzzle.mistakeLeftOne": "Il reste 1 erreur.",
    "dailyPuzzle.mistakesLeftTemplate": "Il reste {n} erreurs.",
    "dailyPuzzle.repeatGuess": "Vous avez déjà essayé ces quatre-là.",
    "dailyPuzzle.won": "Résolu ! Vous avez trouvé les quatre groupes.",
    "dailyPuzzle.lost": "Plus d'erreurs permises. Voici les groupes manqués.",
    "dailyPuzzle.missedHeading": "Groupes manqués",
    "dailyPuzzle.streakOne": "Résolu 1 jour de suite",
    "dailyPuzzle.streakTemplate": "Résolu {n} jours de suite",
    "dailyPuzzle.copyResult": "Copier le résultat",
    "dailyPuzzle.copied": "Copié",
    "dailyPuzzle.copyManual": "La copie n'a pas fonctionné ici. Sélectionnez et copiez ce texte.",
    "dailyPuzzle.copyAria": "Résultat à copier",
    "dailyPuzzle.resultHeadingTemplate": "Groupes égaux n° {n} · {level}",
    "dailyPuzzle.practice": "Jouer un casse-tête d'entraînement",
    "dailyPuzzle.practiceAnother": "Jouer un autre casse-tête d'entraînement",
    "dailyPuzzle.backToDaily": "Revenir au casse-tête du jour",
} satisfies Record<DailyPuzzleStringKey, string>;

export const dailyPuzzleStrings: Record<Locale, Record<DailyPuzzleStringKey, string>> = { en, es, fr };
